import { type FC, useState, useEffect, useRef } from "react";

import { Loader2 } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { useProcessingChecklist } from "@/shared/hooks/useProcessingChecklist";
import { ProcessingChecklist } from "@/shared/components/ProcessingChecklist";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { supabase } from "@/shared/lib/supabase";
import { ADMIN_TASK_IDS } from "@/shared/constants/taskIds";

import type { FlowCustomComponentProps } from "@/shared/constants/flowTypes";

const CHECKLIST_ITEMS = [
  "Confirming who the court includes",
  "Checking who the court needs notified",
  "Building the sign-off list",
] as const;

export const AdminLoadingScreen: FC<FlowCustomComponentProps> = ({ onComplete, answers }) => {
  const { person } = useAppContext();
  const estateId = person?.estate_id;
  const queryClient = useQueryClient();
  const calledRef = useRef(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!estateId || calledRef.current) return;
    calledRef.current = true;

    (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const { error } = await supabase.functions.invoke("legal/probate/compute-distributees", {
          body: { estateId, taskId: ADMIN_TASK_IDS.FAMILY_TRIAGE, answers },
          headers: session?.access_token
            ? { Authorization: `Bearer ${session.access_token}` }
            : undefined,
        });

        if (error) {
          console.error("Compute distributees failed:", error);
          setFailed(true);
          setDone(true);
          return;
        }

        await queryClient.invalidateQueries({ queryKey: ["person-contacts"] });
        setDone(true);
      } catch (err) {
        console.error("Compute distributees error:", err);
        setFailed(true);
        setDone(true);
      }
    })();
  }, [estateId, queryClient]);

  const { activeIndex, isDone } = useProcessingChecklist({
    itemCount: CHECKLIST_ITEMS.length,
    isTerminal: done,
    failed,
    onComplete: () => onComplete(),
    onFailed: () => {
      enqueueSnackbar("Something went wrong building your list. The results may be incomplete.", { variant: "error" });
      onComplete();
    },
  });

  return (
    <div className="flex flex-1 flex-col items-center justify-center text-center -mt-8 min-h-[60vh]">
      <Loader2 className="size-12 animate-spin text-brand" />

      <h4 className="mt-6">Building your list for the court</h4>

      <ProcessingChecklist
        items={CHECKLIST_ITEMS}
        activeIndex={activeIndex}
        isDone={isDone}
      />
    </div>
  );
};
